import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV, CONTACT, EKTIREN, waLink } from "@/data/site";
import logo from "@/assets/brand/logo.jpg";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      {/* Ektiren top strip */}
      <div
        data-testid="ektiren-top-strip"
        className="w-full bg-gradient-to-r from-cobalt via-neon-purple to-neon-magenta text-white text-[11px] sm:text-xs font-mono tracking-wide"
      >
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-2 text-center">
          <span className="opacity-80">Tasarım &amp; Geliştirme:</span>
          <span className="font-bold">{EKTIREN.name}</span>
          <span className="hidden sm:inline opacity-80">· {EKTIREN.phone}</span>
        </div>
      </div>

      <header
        data-testid="site-navbar"
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-strong border-b border-white/10" : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-[70px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src={logo} alt="İzmir Medya" className="h-11 w-11 rounded-full ring-1 ring-white/20 group-hover:ring-neon-magenta transition-all" />
            <span className="font-display font-extrabold text-lg leading-tight">
              İzmir<span className="text-gradient inline-block pr-[0.12em] ml-[0.28em]">Medya</span>
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <li key={n.path}>
                <Link
                  to={n.path}
                  data-testid={`nav-link-${n.path === "/" ? "home" : n.path.slice(1)}`}
                  className={`relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    pathname === n.path ? "text-white" : "text-slate-400 hover:text-white"
                  }`}
                >
                  {n.label}
                  {pathname === n.path && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-0 -z-10 rounded-full bg-white/10 border border-white/10"
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href={waLink(CONTACT.phoneRaw, "Merhaba İzmir Medya, sosyal medya paketleriniz hakkında bilgi almak istiyorum.")}
              target="_blank" rel="noreferrer"
              data-testid="navbar-whatsapp-cta"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cobalt to-neon-magenta text-white text-sm font-semibold hover:scale-[1.03] transition-transform glow-magenta"
            >
              <MessageCircle className="w-4 h-4" /> Teklif Al
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-white"
              aria-label="Menü"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden glass-strong border-t border-white/10"
            >
              <ul className="px-4 py-4 flex flex-col gap-1">
                {NAV.map((n) => (
                  <li key={n.path}>
                    <Link
                      to={n.path}
                      data-testid={`mobile-nav-${n.path === "/" ? "home" : n.path.slice(1)}`}
                      className={`block px-4 py-3 rounded-xl text-base font-medium ${
                        pathname === n.path ? "bg-white/10 text-white" : "text-slate-300"
                      }`}
                    >
                      {n.label}
                    </Link>
                  </li>
                ))}
                <a
                  href={waLink(CONTACT.phoneRaw, "Merhaba İzmir Medya, bilgi almak istiyorum.")}
                  target="_blank" rel="noreferrer"
                  className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cobalt to-neon-magenta text-white font-semibold"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp'tan Teklif Al
                </a>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
